import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import axios from 'axios';

interface MerchantBranding {
  logo: string | null;
  primaryColor: string;
  secondaryColor: string;
  tagline: string;
  loginSlides?: Array<{ url: string; caption?: string }>;
}

interface Merchant {
  id: string;
  name: string;
  subdomain: string;
  branding: MerchantBranding;
  paystackPublicKey?: string;
}

interface MerchantContextType {
  merchant: Merchant | null;
  loading: boolean;
  subdomain: string | null;
  isMainDomain: boolean;
  selectedMerchantId: string | null;
  setSelectedMerchantId: (id: string | null) => void;
  selectedMerchantSubdomain: string | null;
  setSelectedMerchantSubdomain: (subdomain: string | null) => void;
  refreshSubscription: () => Promise<void>;
  paystackPublicKey: string | null;
}

const MerchantContext = createContext<MerchantContextType | null>(null);

export const MerchantProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [merchant, setMerchant] = useState<Merchant | null>(null);
  const [loading, setLoading] = useState(true);
  const [subdomain, setSubdomain] = useState<string | null>(null);
  const [isMainDomain, setIsMainDomain] = useState(true);
  const [selectedMerchantId, setSelectedMerchantId] = useState<string | null>(null);
  const [selectedMerchantSubdomain, setSelectedMerchantSubdomain] = useState<string | null>(null);

  // Load selected merchant from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('superAdminSelectedMerchantId');
    if (saved) {
      setSelectedMerchantId(saved);
    }
    const savedSubdomain = localStorage.getItem('superAdminSelectedMerchantSubdomain');
    if (savedSubdomain) {
      setSelectedMerchantSubdomain(savedSubdomain);
    }
  }, []);

  // Save selected merchant to localStorage whenever it changes
  useEffect(() => {
    if (selectedMerchantId) {
      localStorage.setItem('superAdminSelectedMerchantId', selectedMerchantId);
    } else {
      localStorage.removeItem('superAdminSelectedMerchantId');
    }
  }, [selectedMerchantId]);

  useEffect(() => {
    if (selectedMerchantSubdomain) {
      localStorage.setItem('superAdminSelectedMerchantSubdomain', selectedMerchantSubdomain);
    } else {
      localStorage.removeItem('superAdminSelectedMerchantSubdomain');
    }
  }, [selectedMerchantSubdomain]);

  useEffect(() => {
    const detectSubdomainAndFetchMerchant = async () => {
      try {
        // Extract subdomain from current URL
        const hostname = window.location.hostname;
        const parts = hostname.split('.');
        
        console.log('🌐 Hostname:', hostname);
        console.log('🌐 Parts:', parts);
        
        let detectedSubdomain: string | null = null;
        
        // Development: faith.localhost
        if (parts.length >= 2 && parts[parts.length - 1] === 'localhost') {
          if (parts.length === 2 && parts[0] !== 'localhost' && parts[0] !== 'app') {
            detectedSubdomain = parts[0];
          }
          // app.localhost = main domain
          else if (parts.length === 2 && parts[0] === 'app') {
            detectedSubdomain = null;
          }
        }
        // Production: app.thechurchhq.com = main domain, faith.thechurchhq.com = church subdomain
        else if (parts.length >= 3) {
          if (parts[0] === 'app') {
            detectedSubdomain = null;
          } else {
            detectedSubdomain = parts[0];
          }
        }
        
        console.log('🌐 Detected subdomain:', detectedSubdomain);
        
        setSubdomain(detectedSubdomain);
        setIsMainDomain(!detectedSubdomain);
        
        // If we have a subdomain, fetch merchant data
        if (detectedSubdomain) {
          console.log('🌐 Fetching merchant data for:', detectedSubdomain);
          
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/merchants/by-subdomain/${detectedSubdomain}`
          );
          
          if (response.data.success) {
            console.log('🌐 Merchant data loaded:', response.data.data.merchant);
            setMerchant(response.data.data.merchant);
            
            // ✅ Apply merchant colors to CSS variables
            const root = document.documentElement;
            root.style.setProperty('--primary-color', response.data.data.merchant.branding.primaryColor);
            root.style.setProperty('--secondary-color', response.data.data.merchant.branding.secondaryColor);
          }
        } else {
          console.log('🌐 Main domain - no merchant branding');
        }
        
      } catch (error) {
        console.error('🌐 Error fetching merchant:', error);
        // Don't fail the app if merchant fetch fails
      } finally {
        setLoading(false);
      }
    };

    detectSubdomainAndFetchMerchant();
  }, []);

  // ✅ Helper function to refresh subscription when merchant changes
  const refreshSubscription = async () => {
    try {
      // This will trigger a subscription update in AuthContext
      // when used in conjunction with useAuth's fetchAndUpdateSubscription
      console.log('🔄 Refreshing subscription data for merchant:', selectedMerchantId || subdomain);
    } catch (error) {
      console.error('Failed to refresh subscription:', error);
    }
  };

  const value: MerchantContextType = {
    merchant,
    loading,
    subdomain,
    isMainDomain,
    selectedMerchantId,
    setSelectedMerchantId,
    selectedMerchantSubdomain,
    setSelectedMerchantSubdomain,
    refreshSubscription,
    paystackPublicKey: merchant?.paystackPublicKey || null,
  };

  return (
    <MerchantContext.Provider value={value}>
      {children}
    </MerchantContext.Provider>
  );
};

export const useMerchant = (): MerchantContextType => {
  const context = useContext(MerchantContext);
  if (!context) {
    throw new Error('useMerchant must be used within a MerchantProvider');
  }
  return context;
};

export default MerchantContext;